import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Paper,
} from '@mui/material';

import BusinessIcon from '@mui/icons-material/Business';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ApartmentIcon from '@mui/icons-material/Apartment';

import Chart from "react-apexcharts";

import { useNotification } from 'config/Notification';

import { fetchCompaniesService } from "services/modules/idp/company";
import { fetchDomainsPerfomance as fetchDomainsPerfomanceService, fetchDomainsHeatmapPerfomance as fetchDomainsHeatmapPerfomanceService } from "services/modules/ecs/data";
import { setCompanyData as setCompanyDataService} from "stores/modules/idp/authreducer";

const PartnerDashboardPage = () => {
  // Redux dispatch
  const dispatch = useDispatch();

  // React router
  const navigate = useNavigate();

  // Notification
  const {openNotification} = useNotification();

  // Redux selectors
  const authenticatedUserContainer = useSelector((state) => state.auth.user);
  const dimensionsContainer = useSelector((state) => state.ecsData.dimensions);
  const companyDataContainer = useSelector((state) => state.company.companies);

  // State variables
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [tableColumns, setTableColumns] = useState([]);
  const [chooseDialog,setChooseDialog]=useState(false);
  
  // useEffect hook sets the initial value of company dropdown to the company of user who logs in
  useEffect(() => {
    if(authenticatedUserContainer){
      const company = authenticatedUserContainer.company;
      setSelectedCompany(company);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[authenticatedUserContainer]);
  
  // useEffect hook sets the table header values
  useEffect(() => {
    if(tableData && tableData.length> 0){
      let columns = [
        {"name":"domain", "label":"Company"},
        {"name":"overall", "label":"Overall"},
      ];
      const filteredDimensions = dimensionsContainer.filter((dim) => !!dim.subDimensions);
      Object.keys(tableData[0]).forEach((key) => {
        const value = filteredDimensions.find((dimension)=>
          dimension.slug === key
        );
        if (value){
          columns.push({
            "name": key,
            "label": value.label
          });
        }        
      });
      setTableColumns(columns);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[tableData]);
  
  // useEffect fetches domains data and displays in the table cells
  useEffect(() => {
    const fetchDomainsData = async() => {
      let arrayofdomains = [selectedCompany.domain];
      let supplierheatmap = false;
      let suppliers = {};
      setHeatmap(false);
      let supplierdomains = [];
      if (selectedCompany.heatmap && selectedCompany.heatmap === "true"){
        supplierheatmap = true;
      }
      if (selectedCompany.customers && (selectedCompany.customers.length > 0)){
        for (const customer of selectedCompany.customers){
          if(customer?.applications?.some(app => app.slug === 'crs')){
            arrayofdomains.push(customer.domain);
            if (supplierheatmap && customer.supplier_type){
              supplierdomains.push(customer.domain);
              suppliers[customer.domain] = customer.supplier_type;
            }
          }
        }
      }

      // Get Domains performance based on ECS Score
      let params = {
        arrayOf_domain: arrayofdomains.join(",")
      };
      const domainsresponse = await fetchDomainsPerfomanceService({params});
      if (domainsresponse.status === 200){
        const domainsData = domainsresponse.data.map((score) => {
          const newScore = { ...score };

          Object.keys(newScore.scores).forEach((key) => {
            newScore[key] = newScore.scores[key];
          });
          delete newScore.scores;
          Object.keys(newScore).forEach((key) => {
            if (key !== "domain"){
              newScore[key] = getLevelByScore(newScore[key]);
            }
          });
          return newScore;
        });
        setTableData(domainsData);
      }
      else{
        openNotification("Server error for domain performance",'error');
      }

      if(supplierheatmap && supplierdomains.length > 0){
        let params = {
          arrayOf_domain: supplierdomains.join(",")
        };
        const heatmapresponse = await fetchDomainsHeatmapPerfomanceService({params});
        if (heatmapresponse.status === 200){

          let chartSeries = chartstate.series;
          const getCompaniesScoresforHeatmap = heatmapresponse.data;
          let count = 1;

          for (const supplier of getCompaniesScoresforHeatmap){
            const score = supplier.score;
            if (score >= 0 && score < 4){
              for (let suppliertype of chartSeries[0].data){
                if (suppliertype.x === suppliers[supplier.domain]){
                  if (!supplier.text){
                    suppliertype.text = "S"+count
                    suppliertype.domain = supplier.domain;
                    count = count+1;
                  }
                }
              }
            }
            else if (score >= 4 && score < 7){
              for (let suppliertype of chartSeries[1].data){
                if (suppliertype.x === suppliers[supplier.domain]){
                  if (!supplier.text){
                    suppliertype.text = "S"+count
                    suppliertype.domain = supplier.domain;
                    count = count+1;
                  }
                }
              }
            }
            else if (score >= 7 && score < 9) {
              for (let suppliertype of chartSeries[2].data){
                if (suppliertype.x === suppliers[supplier.domain]){
                  if (!supplier.text){
                    suppliertype.text = "S"+count
                    suppliertype.domain = supplier.domain;
                    count = count+1;
                  }
                }
              }
            }
            else if (score >= 9) {
              for (let suppliertype of chartSeries[3].data){
                if (suppliertype.x === suppliers[supplier.domain]){
                  if (!supplier.text){
                    suppliertype.text = "S"+count
                    suppliertype.domain = supplier.domain;
                    count = count+1;
                  }
                }
              }
            }            
          }
          setHeatmap(true);
          chartstate.options['dataLabels'] = {
            enabled: true,
            style: {
              colors: ['#000000'] 
            },
            formatter: function (val, opts) {
              const text = opts.w.config.series[opts.seriesIndex].data[opts.dataPointIndex].text; 
              return text;
            }
          }
          chartstate.options.chart['events'] = {
            click: function (event, chartContext, config) {
              let el = event.target;
              let seriesIndex = parseInt(el.getAttribute("i"));
              let dataPointIndex = parseInt(el.getAttribute("j"));
              if (config.config.series[seriesIndex].data[dataPointIndex].domain){
                viewDashboard(config.config.series[seriesIndex].data[dataPointIndex].domain);
              }
            }
          }
          chartstate.series  = chartSeries;
          chartstate.options.tooltip = {
            enabled: true,
            y:{
            formatter: function(val, opts) {
              const domain = opts.w.config.series[opts.seriesIndex].data[opts.dataPointIndex].domain;
              return domain;
            }
          }
          };
          setHeatmapOptions(chartstate);
        }
        else{
          openNotification('Server error for heatmap','error')
        }
      }
    };

    if (selectedCompany){
      fetchDomainsData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[selectedCompany]);

  // This method returns level based on score
  function getLevelByScore(score) {
    if (score === 0) {
      return "A";
    }
    if (score > 0 && score < 4) {
      return "B";
    }

    if (score >= 4 && score < 7) {
      return "C";
    }

    if (score >= 7 && score < 9) {
      return "D";
    }

    if (score >= 9) {
      return "F";
    }
  };

  // This method handles change in selected company
  const onInputChange = (event, value, reason) => {
    if (value) {
      let params = {
        page:'1',
        limit:20,
        sortBy:'-createdAt',
        search: value,
        partner: "null",
      };
      if (value !== authenticatedUserContainer.company.name){
        dispatch(fetchCompaniesService({params}));
      }
    } 
  };
  
  // This method handles navigation  to overall-dashboard when clicked on a partner customer
  function viewDashboard(domain){
    let company = {};
    if (domain === selectedCompany.domain){
      company = {
        companyid : selectedCompany.id,
        companyname : selectedCompany.name,
        domain: selectedCompany.domain
      };
    }
    else{
      if(selectedCompany.customers){
        const customer = selectedCompany.customers.find((customer)=>
          customer.domain === domain
        );
        company = {
          companyid : customer.id,
          companyname : customer.name,
          domain: customer.domain
        };
      }
    }
    dispatch(setCompanyDataService(company));
    navigate(`/crs/dashboard/over-all`);
  }

  // This method checks whether the given company is a partner or not based on domain
  function checkPartner(domain){
    if (selectedCompany && (domain === selectedCompany.domain)){
      if (selectedCompany.subscriptionPlan &&
          selectedCompany.subscriptionPlan.slug.includes(
            'partner'
          )
        ){
          return true;
        }
    }
    return false
  }

  // This method checks whether the given company is a partner customer or not based on domain
  function checkPartnerCustomer(domain){
    if(selectedCompany && selectedCompany.customers){
      const customer = selectedCompany.customers.find((customer)=>
        customer.domain === domain
      );
      if (customer){
        return true
      }
    }
    return false
  }

  // Heatmap Graph
  let chartstate = {
    options: {
      chart: {
        height: 350,
        type: 'heatmap',
        toolbar: {
          show: false
        },
      },
      stroke: {
        show: true,
        curve: 'smooth',
        lineCap: 'butt',
        width: 1,
        colors: ["#90A4AE"],
        dashArray: 0
      },
      grid: {
        show: true,
        borderColor: '#90A4AE',
        strokeDashArray: 0
      },
      plotOptions: {
        heatmap: {
          enableShades: false,
          radius: 0,
          useFillColorAsStroke: false,
          colorScale: {
            ranges: [
            {
              from: 0,
              to: 3.99,
              name: 'Low',
              color: '#9ac356'
            }, {
              from: 4,
              to: 6.99,
              name: 'Medium',
              color: '#fcdd50'
            }, {
              from: 7,
              to: 8.99,
              name: 'High',
              color: '#e56c39'
            }, {
              from: 9,
              to: 20,
              name: 'Critical',
              color: '#db3832'
            }]
          }
        }
      },
    },
    series: [
      {
        name: "Low",
        data: [
          {
            x: 'Insignificant',
            y: 3,
          },
          {
            x: 'Routine',
            y: 0
          },
          {
            x: 'Preferred',
            y: 6,
          },
          {
            x: 'Strategic',
            y: 8
          }
        ]
      },
      {
        name: "Medium",
        data: [
          {
            x: 'Insignificant',
            y: 1
          },
          {
            x: 'Routine',
            y: 6
          },
          {
            x: 'Preferred',
            y: 8,
          },
          {
            x: 'Strategic',
            y: 20,
          }
        ]
      },
    {
      name: "High",
      data: [{
        x: 'Insignificant',
        y: 6,
      }, {
        x: 'Routine',
        y: 8
      }, {
        x: 'Preferred',
        y: 8,
      }, {
        x: 'Strategic',
        y: 20,
      }]
    },
    {
      name: "Critical",
      data: [{
        x: 'Insignificant',
        y: 9
      }, {
        x: 'Routine',
        y: 18,
      }, {
        x: 'Preferred',
        y: 17,
      }, {
        x: 'Strategic',
        y: 20
      }]
    }],
  };

  // State variables
  const [heatmapOptions, setHeatmapOptions] = useState(chartstate);
  const [heatmap, setHeatmap] = useState(false);
  
  return (
    <div className='card-container'>
      <div className="main-container partner-dashboard-container">
        <div className={heatmap ? "heatmap-container" : "heatmap-container-none" } >
          <div className="row" >
            <Chart
              className="heatmap"
              options={heatmapOptions.options}
              series={heatmapOptions.series}
              type="heatmap"
              height="350"
              width="400"
            />
          </div>
        </div>
        <div className='partner-dashboard-buttons'>
          <div className="row">
            <div className="col-xs-12 col-sm-12 col-md-6 col-lg-4">
              <div className="row partner-dashboard-company">
                {(authenticatedUserContainer &&
                  authenticatedUserContainer.roles &&
                  authenticatedUserContainer.roles.some((role) =>
                    role.permissions.includes('GET_ALL_COMPANIES')
                  )
                  ) && (
                    <Autocomplete
                      options={companyDataContainer.results || []}
                      onInputChange={onInputChange}
                      getOptionLabel={(option) => option?.name || ''}
                      value={selectedCompany}
                      onChange={(e,value) => setSelectedCompany(value)}
                      renderInput={(params) => (
                        <TextField {...params}
                        label="Company"
                        variant="outlined"
                        size="small"
                          />
                      )}
                    />
                  )}
              </div>
            </div>
            <div className="col-xs-12 col-sm-12 col-md-6 col-lg-8">
              <div className="partner-dashboard-actions">
                {(authenticatedUserContainer &&
                  authenticatedUserContainer.roles &&
                  authenticatedUserContainer.roles.some((role) =>
                    role.permissions.includes('CREATE_ANY_COMPANIES')
                  )
                  ) && (
                    <Button variant="contained" className="add-company font" onClick={() => setChooseDialog(true)}>Add company</Button>
                  )}
              </div>
            </div>
          </div>
        </div>
        <div className='partner-dashboard'>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  {tableColumns.map((column) => (
                    <TableCell key={column.name}>{column.label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {tableData.map((row, index) => (
                  <TableRow key={index}>
                    {tableColumns.map((column) => (
                      <TableCell key={column.name}>
                        {column.name === 'domain' ? (
                          <div className="sub_name_cont">
                            <div className="business-container">
                              <BusinessIcon className="business" />
                            </div>
                            <span className='text-bold partner-name-text' onClick={() => viewDashboard(row[column.name])} >{row[column.name]}</span>
                            <div className={checkPartner(row[column.name]) ? "partner-company-container" : "partner-company-container-none"}>
                              <LocalOfferIcon fontSize="small" className='partner' />
                            </div>
                            <div className={checkPartnerCustomer(row[column.name]) ? "partner-customer-container" : "partner-customer-container-none"} >
                              <ApartmentIcon fontSize="small" className="partner" />
                            </div>
                          </div>
                        ) : (
                          <div className='rating-value'>
                            <span className={row[column.name]}>{row[column.name]}</span>
                          </div>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Dialog
            open={chooseDialog}
            onClose={() => setChooseDialog(false)}
            aria-labelledby="delete-dialog-title"
          >
            <DialogTitle  align="center">
              Choose customer type
              <Divider className="divider" />
            </DialogTitle>
            <DialogActions className="m-3">
              <Button variant="contained" onClick={()=>navigate(`/add-partnercompany`,{state:{customertype:'Partner'}})} >
                Partner
              </Button>
              <Button variant="contained" onClick={()=>navigate(`/add-partnercompany`,{state:{customertype:'endcustomer'}})} >
                End customer
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      </div>
    </div>
  )
}

export default PartnerDashboardPage;
